import {
  Entity,
  IIdentifiableEntity,
  IEntityType,
  EntityStringId,
  EntityFields,
  EntityId,
  IExternalAssociation,
} from 'app/types/entity';
import * as logger from 'app/services/logger';
import {
  updateFromServer,
  serializeEntity,
  getRemoteMap,
  entityDeleted,
} from 'app/remote';
import vmClient from 'app/services/vmClient';
import { AppDispatch, AppGetState, AnyAction, AnyAppAction } from 'redux';
import { sessionRequest } from 'app/models/auth/actions';
import { RequestOptions } from 'app/utils/request';
import { CancelToken } from 'app/utils/cancelToken';
import { getResponseId } from 'app/remote/response';
import { onlyApplicationError } from 'app/remote/error';
import { DESERIALIZATION_NOT_FOUND } from 'app/constants/ServerErrors';
import { formatGenericError } from 'app/utils/errors';
import { batchActions } from 'redux-batched-actions';
import {
  saveEntity,
  saveEntityDeep,
} from './base';
import { calculateVersions } from '@engoo/view-model';
import { getVersionParams } from './versions';
import { AppThunk } from 'app/store';

interface IEntityDatabase {
  getIn<T extends IIdentifiableEntity<EntityId>>(key: [string, T['attributes']['id']]): Entity<T> | undefined;
  setIn<T extends IIdentifiableEntity<EntityId>>(key: [string, T['attributes']['id']], entity: Entity<T>): this;
}

/**
 * Adds a new entity to the remote. Returns its `id`.
 */
export const createRemoteEntity = <T extends IIdentifiableEntity<EntityId>>(
  entity: Entity<T>,
  request: (data: unknown) => RequestOptions,
) => async (dispatch: AppDispatch): Promise<unknown> => {
  const payload = dispatch(generatePayload(entity.schema, entity.id, (state) => state.setIn(
    [entity.schema.storePrefix, entity.id],
    entity,
  )));

  const response = await dispatch(sessionRequest(request(payload)));

  dispatch(updateFromServer(response));

  return response;
};

const generatePayload = <Schema extends IIdentifiableEntity<EntityId>>(
  schema: IEntityType<Schema>,
  id: Schema['attributes']['id'],
  update: (state: IEntityDatabase) => IEntityDatabase,
) =>
  (dispatch: AppDispatch) => dispatch((_, getState): unknown => {
    const state: IEntityDatabase = getRemoteMap(getState());
    const speculativeState = update(state);

    // Need to ts-ignore because ExtendedViewModelClient is
    // JavaScript class and serializeEdits is inherited
    // @ts-ignore
    return vmClient.serializeEdits(
      speculativeState,
      state,
      schema,
      id,
    );
  });

type Thunk = AppThunk<Promise<unknown>>;

export type EntityFetcher = (
  id: EntityStringId,
  cancelToken?: CancelToken,
) => Thunk;

export type EntityCreator = <Data>(
  data?: Data,
  cancelToken?: CancelToken,
) => (
  dispatch: AppDispatch,
  getState: AppGetState,
) => Promise<EntityStringId>;

export const entityActions = (
  routes: {
    getById: (id: EntityStringId) => RequestOptions,
    delete: (id: EntityStringId) => RequestOptions,
    post: (data: unknown) => RequestOptions,
    get: (params: unknown) => RequestOptions,
    search: (params: unknown) => RequestOptions,
  },
  type: IEntityType<IIdentifiableEntity>,
): {
  fetch: EntityFetcher,
  fetchList: (params?: unknown, cancelToken?: CancelToken) => Thunk,
  search: (params?: unknown, cancelToken?: CancelToken) => Thunk,
  remove: (id: EntityStringId) => Thunk,
  save: (id: EntityStringId) => Thunk,
} => {
  const fetchAction: EntityFetcher = (id, cancelToken) => fetchEntity({
    id,
    type,
    params: routes.getById(id),
    cancelToken,
  });

  return ({
    fetch:     fetchAction,
    fetchList: (params, cancelToken) => (
      fetchEntities({
        params: routes.get(params),
        type,
        cancelToken,
      })
    ),
    search: (params, cancelToken) => (
      fetchEntities({
        params: routes.search(params),
        type,
        cancelToken,
      })
    ),
    remove:    removeEntity((id: EntityStringId) => sessionRequest(routes.delete(id)), type),
    save:      saveEntity(fetchAction, (data: unknown) => sessionRequest(routes.post(data)), type),
  });
};

export const postAndUpdate = (postAction: AnyAction) => (dispatch: AppDispatch): Promise<EntityStringId> =>
  dispatch(postAction)
    .then((response: unknown): EntityStringId => {
      dispatch(updateFromServer(response));
      return getResponseId(response);
    })
    .catch((error: unknown) => {
      logger.error(formatGenericError(error));
      return Promise.reject(error);
    });

export const createEntity = <T extends IIdentifiableEntity>(
  postEntity: (data: unknown, cancelToken?: CancelToken) => AnyAction,
  valueBase: Entity<T>,
): EntityCreator => (
  valueCustom?: Partial<EntityFields<T>>,
  cancelToken?: CancelToken,
) => (
  postAndUpdate(postEntity(
    serializeEntity(valueBase.mergeDeep(valueCustom ?? {})),
    cancelToken,
  ))
);

interface ICanonicalArgs {
  [index: string]: ICanonicalArgs | string[] | string | undefined;
}

export const externalAssociationActions = (
  routes: {
    get: (id: EntityStringId, args: unknown) => RequestOptions,
    post: (id: EntityStringId, data: unknown) => RequestOptions,
    search: (id: EntityStringId, params: unknown) => RequestOptions,
  },
  type: IExternalAssociation,
  options: {
    canonicalArgs?: ICanonicalArgs,
  } = {},
): {
  fetch: (id: EntityStringId, params?: unknown, cancelToken?: CancelToken) => Thunk,
  fetchCanonical: (id: EntityStringId, cancelToken?: CancelToken) => Thunk,
  search: (id: EntityStringId, params?: unknown, cancelToken?: CancelToken) => Thunk,
  save: (id: EntityStringId) => Thunk,
  saveDeep: (id: EntityStringId) => Thunk,
} => {
  // page_size = 0 means fetch all
  const fetchCanonicalArgs = { page_size: 0, ...options.canonicalArgs };

  const fetchCanonicalAction = (id: EntityStringId, cancelToken?: CancelToken) => fetchExternalAssociation({
    id,
    type,
    params: routes.get(id, fetchCanonicalArgs),
    cancelToken,
  });
  const saveAction = (id: EntityStringId) => saveEntity(fetchCanonicalAction, (data: unknown) =>
    sessionRequest(routes.post(id, data)), type)(id);

  const saveDeep = (id: EntityStringId) => saveEntityDeep(fetchCanonicalAction, (data: unknown) =>
    sessionRequest(routes.post(id, data)), type)(id);

  return ({
    fetch: (id, params, cancelToken) => (
      fetchEntities({
        params: routes.get(id, params),
        type,
        cancelToken,
      })
    ),
    fetchCanonical: fetchCanonicalAction,
    search: (id, params, cancelToken) => (
      fetchEntities({
        params: routes.search(id, params),
        type,
        cancelToken,
      })
    ),
    save: saveAction,
    saveDeep,
  });
};


/**
 * `fetchEntities` fetches using the given params, adding the versions for the
 * given entity `type` to the query params, and updates the store with the
 * resulting data, and returns the response.
 *
 * - `params` are the params for `request`, but the version query params will be
 *   merged into this automatically
 * - `type` is the entity type being fetched, used to generate the versions for
 *   the query params
 * - `fetchAction` can optionally be used to override what function is used to
 *   actually fetch the response with the final params. By default it uses
 *   `sessionRequest`, but in some cases it needs to be overridden to use plain
 *   `request` (such as when fetching auth related data). `fetchEntities`
 *   returns whatever `fetchAction` returned.
 * - `updateAction` can be used to control how the response is used to update
 *   the store. This is mainly needed to override the behavior for
 *   `fetchExternalAssociation`, as well as a few special cases where the backend
 *   returns a response with an unusual structure (such as the daily news timeline
 *   categories).
 */
export const fetchEntities = <ParamsType extends RequestOptions>({
  params,
  type,
  fetchAction = sessionRequest,
  updateAction = updateFromServer,
  cancelToken,
}: {
  params: ParamsType,
  type: IEntityType<any> | IExternalAssociation,
  fetchAction?: (params: ParamsType) => (dispatch: AppDispatch) => Promise<unknown>,
  cancelToken?: CancelToken,
  updateAction?: (response: unknown) => AnyAppAction,
}) => async (dispatch: AppDispatch) => {
  const versionParams = getVersionParams(calculateVersions(type));
  const finalParams = {
    ...params,
    data: {
      ...params.data,
      ...versionParams,
    },
    cancelToken,
  };
  const response = await dispatch(fetchAction(finalParams));
  dispatch(updateAction(response));
  return response;
};

export const fetchEntity = <ParamsType extends RequestOptions>(options: {
  id: EntityStringId,
  type: IEntityType<any>,
  params: ParamsType,
  fetchAction?: (params: ParamsType) => (dispatch: AppDispatch) => Promise<unknown>,
  cancelToken?: CancelToken,
}) => async (dispatch: AppDispatch) => {
  try {
    return await dispatch(fetchEntities(options));
  } catch (error) {
    const wrappedError = onlyApplicationError(error);
    if (wrappedError?.code === DESERIALIZATION_NOT_FOUND) {
      // TODO: this may be too strong an assertion; the entity was missing, assumed deleted
      dispatch(removeRelatedEntities(options.type, options.id));
    }
    throw error;
  }
};

export const fetchExternalAssociation = <ParamsType extends RequestOptions>(options: {
  id: EntityStringId,
  type: IExternalAssociation,
  params: ParamsType,
  fetchAction?: (params: ParamsType) => (dispatch: AppDispatch) => Promise<unknown>,
  cancelToken?: CancelToken,
}) => async (dispatch: AppDispatch) => {
  return await dispatch(fetchEntities({
    ...options,
    updateAction: (response) => updateFromServer(response, options.type, options.id),
  }));
};

/**
 * @param {function} deleteEntity
 * @param {any} type An entity constructors.
 */
export const removeEntity = (
  deleteEntity: (id: EntityStringId) => Thunk,
  type: IEntityType<any>,
) => (id: EntityStringId) => async (dispatch: AppDispatch) => {
  const response = await dispatch(deleteEntity(id));
  dispatch(removeRelatedEntities(type, id));
  return response;
};

/**
 * Helper for batching the deletion of an entity across multiple tables.
 */
const removeRelatedEntities = (type: IEntityType<any>, id: EntityStringId) =>
  batchActions([
    ...type.relatedTypes.map((relatedType) => entityDeleted(relatedType, id)),
    ...type.externalAssociationTypes.map((associatedType) => entityDeleted(associatedType, id)),
  ]);
